import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Play, Pause, Clock, ChevronLeft, ChevronRight, Grid, ArrowRight, Home, Monitor } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import KanbanBoardContainer from './KanbanBoardContainer';
import { TVBoardProvider, useTVBoard } from '../context/TVBoardContext';
import { useAppState } from '../context/AppStateContext';

interface RotationIntervalConfig {
    value: number;
    displayName: string;
}

// Centralized rotation interval configuration
const ROTATION_INTERVALS: RotationIntervalConfig[] = [
    { value: 5, displayName: '5 วิ' },
    { value: 10, displayName: '10 วิ' },
    { value: 15, displayName: '15 วิ' },
    { value: 30, displayName: '30 วิ' },
    { value: 60, displayName: '1 นาที' },
];

const DEFAULT_INTERVAL = ROTATION_INTERVALS[0].value;

const TVBoardInner: React.FC = () => {
    const navigate = useNavigate();
    const { getBoards } = useAppState();
    const boardTypes: string[] = getBoards().map(board => board.key);
    const [currentBoardIndex, setCurrentBoardIndex] = useState(0);
    const [isTransitioning, setIsTransitioning] = useState(false);
    const [isPaused, setIsPaused] = useState(false);
    const [rotationInterval, setRotationInterval] = useState<number>(DEFAULT_INTERVAL);
    const [showControls, setShowControls] = useState(false);

    const isAutoRotationActiveRef = useRef(false); // Track if auto rotation is actually running
    const isInitializedRef = useRef(false); // Track if component is fully initialized
    const { resetPagination, goToNextPage, currentPage, currentPageIndex, pages, displayMode, setDisplayMode, safeMode, setSafeMode } = useTVBoard();

    // Set up the page completion callback to handle board transitions
    const handlePageCompleted = useCallback(() => {
        // Block unwanted calls during component initialization
        if (!isInitializedRef.current) {
            return;
        }

        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentBoardIndex((prevIndex) => {
                return (prevIndex + 1) % boardTypes.length;
            });
            resetPagination();
            setIsTransitioning(false);
        }, 200);
    }, [boardTypes.length, resetPagination]);

    useEffect(() => {
        if (isPaused) {
            isAutoRotationActiveRef.current = false;
            return;
        }

        isAutoRotationActiveRef.current = true;
        isInitializedRef.current = true; // Mark as initialized when auto-rotation starts
        const interval = setInterval(() => {
            // Advance to next page within current board, or switch to next board when pages are exhausted
            // Pass handlePageCompleted directly as callback to avoid timing issues
            goToNextPage(handlePageCompleted);
        }, rotationInterval * 1000);

        return () => {
            isAutoRotationActiveRef.current = false;
            clearInterval(interval);
        };
    }, [isPaused, rotationInterval, handlePageCompleted, goToNextPage]);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (showControls) {
                const target = event.target as HTMLElement;
                if (!target.closest('.interval-selector')) {
                    setShowControls(false);
                }
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [showControls]);

    const currentBoardType = boardTypes[currentBoardIndex];

    // Get interval display name
    const getIntervalDisplayName = (interval: number): string => {
        const config = ROTATION_INTERVALS.find(config => config.value === interval);
        return config?.displayName || `${interval}s`;
    };

    // Handle TV safe mode toggle - simple toggle between default and padding
    const handleSafeModeToggle = () => {
        setSafeMode(safeMode === 'default' ? 'padding' : 'default');
    };

    // Handle pause/resume toggle
    const handlePauseToggle = () => {
        setIsPaused(!isPaused);
    };

    // Get safe area padding classes based on current safe mode
    const getSafePadding = () => {
        return safeMode === 'padding' ? 'pt-4 pl-4 pr-4' : '';
    };

    // Get control positioning based on current safe mode
    const getControlPosition = () => {
        return safeMode === 'padding' ? 'bottom-8 left-8' : 'bottom-4 left-4';
    };

    // Handle interval change
    const handleIntervalChange = (newInterval: number) => {
        setRotationInterval(newInterval);
        setShowControls(false);
    };

    // Handle manual navigation
    const handlePrevious = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentBoardIndex((prevIndex) => {
                const newIndex = prevIndex === 0 ? boardTypes.length - 1 : prevIndex - 1;
                return newIndex;
            });
            resetPagination();
            setIsTransitioning(false);
        }, 200);
    };

    const handleNext = () => {
        setIsTransitioning(true);
        setTimeout(() => {
            setCurrentBoardIndex((prevIndex) => {
                return (prevIndex + 1) % boardTypes.length;
            });
            resetPagination();
            setIsTransitioning(false);
        }, 200);
    };

    return (
        <div className="relative h-full">
            {/* Board type indicator with enhanced controls - with dynamic TV-safe positioning */}
            <div className={`absolute ${getControlPosition()} z-50 bg-black/70 text-white px-4 py-2 rounded-lg backdrop-blur-sm`}>
                <div className="flex items-center space-x-3">
                    {/* <div className="text-lg font-bold">
                        {getBoardDisplayName(currentBoardType)}
                    </div> */}

                    {/* Progress dots */}
                    <div className="flex space-x-1">
                        {boardTypes.map((_, index) => (
                            <div
                                key={index}
                                className={`w-2 h-2 rounded-full transition-all duration-200 ${index === currentBoardIndex
                                    ? 'bg-blue-400'
                                    : 'bg-gray-400/50'
                                    }`}
                            />
                        ))}
                    </div>

                    {/* Control buttons */}
                    <div className="flex items-center space-x-2">
                        {/* Home button */}
                        <button
                            onClick={() => navigate('/')}
                            className="flex items-center justify-center w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors"
                            title="กลับสู่หน้าหลัก"
                        >
                            <Home className="w-3 h-3" />
                        </button>

                        {/* Navigation buttons */}
                        <button
                            onClick={handlePrevious}
                            className="flex items-center justify-center w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors"
                            title="Previous board"
                        >
                            <ChevronLeft className="w-3 h-3" />
                        </button>

                        <button
                            onClick={handleNext}
                            className="flex items-center justify-center w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors"
                            title="Next board"
                        >
                            <ChevronRight className="w-3 h-3" />
                        </button>

                        {/* Pause/Resume button */}
                        <button
                            onClick={handlePauseToggle}
                            className="flex items-center justify-center w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors"
                            title={isPaused ? 'Resume rotation' : 'Pause rotation'}
                        >
                            {isPaused ? (
                                <Play className="w-3 h-3" />
                            ) : (
                                <Pause className="w-3 h-3" />
                            )}
                        </button>

                        {/* Display mode toggle */}
                        <button
                            onClick={() => setDisplayMode(displayMode === 'scroll' ? 'pagination' : 'scroll')}
                            className="flex items-center justify-center w-6 h-6 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors"
                            title={`Switch to ${displayMode === 'scroll' ? 'page' : 'scroll'} mode`}
                        >
                            {displayMode === 'scroll' ? (
                                <Grid className="w-3 h-3" />
                            ) : (
                                <ArrowRight className="w-3 h-3" />
                            )}
                        </button>

                        {/* TV Safe Mode toggle */}
                        <button
                            onClick={handleSafeModeToggle}
                            className={`flex items-center justify-center w-6 h-6 rounded transition-colors ${safeMode === 'padding'
                                    ? 'bg-orange-600/70 hover:bg-orange-500/70 border border-orange-400/50'
                                    : 'bg-gray-600/50 hover:bg-gray-500/60'
                                }`}
                            title={'TV Safe Mode'}
                        >
                            <Monitor className={`w-3 h-3 ${safeMode === 'padding' ? 'text-orange-100' : ''}`} />
                        </button>



                        {/* Time interval selector */}
                        <div className="relative interval-selector">
                            <button
                                onClick={() => setShowControls(!showControls)}
                                className="flex items-center space-x-1 px-2 py-1 rounded bg-gray-600/50 hover:bg-gray-500/60 transition-colors text-xs"
                                title="Change rotation interval"
                            >
                                <Clock className="w-3 h-3" />
                                <span>{getIntervalDisplayName(rotationInterval)}</span>
                            </button>

                            {/* Dropdown menu */}
                            {showControls && (
                                <div className="absolute bottom-full mb-1 left-0 bg-black/80 rounded-lg shadow-lg border border-gray-600 backdrop-blur-sm">
                                    <div className="py-1">
                                        {ROTATION_INTERVALS.map((config) => (
                                            <button
                                                key={config.value}
                                                onClick={() => handleIntervalChange(config.value)}
                                                className={`block w-full text-left px-3 py-1 text-xs hover:bg-gray-600/50 transition-colors ${rotationInterval === config.value ? 'bg-blue-600/50 text-blue-200' : 'text-gray-300'
                                                    }`}
                                            >
                                                {config.displayName}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Status text with safe mode indicator */}
                    <div className="text-sm text-gray-300">
                        {isPaused ? (
                            <span className="text-yellow-300">Paused</span>
                        ) : (
                            <span>เปลี่ยนทุก {getIntervalDisplayName(rotationInterval)}</span>
                        )}
                    </div>
                </div>
            </div>

            {/* TV Mode Page Indicator - moved from individual board components - with TV-safe positioning */}
            {currentPage && pages.length > 1 && (
                <div className="absolute bottom-24 left-1/2 transform -translate-x-1/2 z-40">
                    <div className="bg-black/70 text-white px-3 py-1 rounded-lg text-sm backdrop-blur-sm">
                        <span className="font-medium">
                            {displayMode === 'scroll' ? (
                                `Columns ${(currentPage.columnStartIndex || 0) + 1}-${(currentPage.columnEndIndex || 0) + 1}`
                            ) : (
                                `Page ${currentPageIndex + 1} of ${pages.length}`
                            )}
                        </span>
                        <span className="text-gray-300 ml-2 text-xs">
                            ({displayMode === 'scroll' ? 'Scroll' : 'Pages'})
                        </span>
                    </div>
                </div>
            )}

            {/* Main content with transition effect and dynamic TV-safe padding */}
            <div
                className={`h-full ${getSafePadding()} transition-opacity duration-200 ${isTransitioning ? 'opacity-50' : 'opacity-100'
                    }`}
            >
                <KanbanBoardContainer
                    key={currentBoardType} // Force re-render when board type changes
                    displayMode="tv"
                    boardType={currentBoardType}
                />
            </div>
        </div>
    );
};

// Main TVBoardContainer component with context provider
const TVBoardContainer: React.FC = () => {
    return (
        <TVBoardProvider>
            <TVBoardInner />
        </TVBoardProvider>
    );
};

export default TVBoardContainer;